import { Store } from '@reduxjs/toolkit';
import { AppDispatch } from '../store';
import { fetchLocations } from '../store/locationsSlice';
import { fetchUsers, setCurrentUser } from '../store/usersSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { fetchInventory, fetchTransactions } from '../store/keyInventorySlice';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchKeyRequests } from '../store/keyRequestsSlice';
import { databaseApi } from '../services/api';

// Initialize data from API
export async function initializeData(store: Store): Promise<void> {
  const dispatch = store.dispatch as AppDispatch;

  try {
    // Check database status and initialize if needed
    try {
      const status = await databaseApi.getStatus();

      if (!status.initialized) {
        // Database is empty, initialize with seed data
        console.log('Initializing database with seed data...');
        await databaseApi.initialize();
      }
    } catch {
      // API not available yet (dev mode or first load)
      console.log('Database status check failed, attempting initialization...');
      try {
        await databaseApi.initialize();
      } catch {
        console.log('Could not initialize database, will retry on data fetch');
      }
    }

    // Fetch all data from API
    await Promise.all([
      dispatch(fetchLocations()),
      dispatch(fetchUsers()),
      dispatch(fetchKeyTypes()),
      dispatch(fetchInventory()),
      dispatch(fetchTransactions()),
      dispatch(fetchAssignments()),
      dispatch(fetchKeyRequests()),
    ]);

    // Set default current user (first admin user)
    const state = store.getState();
    if (state.users.users.length > 0 && !state.users.currentUser) {
      const adminUser = state.users.users.find((u: { role: string }) => u.role === 'admin');
      if (adminUser) {
        dispatch(setCurrentUser(adminUser));
        // Store current user preference in localStorage for session persistence
        localStorage.setItem('keykeeper_currentUserId', adminUser.id);
      }
    } else {
      // Check if we have a stored user preference
      const storedUserId = localStorage.getItem('keykeeper_currentUserId');
      if (storedUserId) {
        const user = state.users.users.find((u: { id: string }) => u.id === storedUserId);
        if (user) {
          dispatch(setCurrentUser(user));
        }
      }
    }
  } catch (error) {
    console.error('Failed to initialize data from API:', error);
    // The app will show loading/error states from the slices
  }
}

// Helper to refresh all data
export async function refreshAllData(dispatch: AppDispatch): Promise<void> {
  await Promise.all([
    dispatch(fetchLocations()),
    dispatch(fetchUsers()),
    dispatch(fetchKeyTypes()),
    dispatch(fetchInventory()),
    dispatch(fetchTransactions()),
    dispatch(fetchAssignments()),
    dispatch(fetchKeyRequests()),
  ]);
}
